import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api, post } from './api.js';
import { errorMessage } from './passkey.js';
import { Alert } from './ui.js';
import { t } from '../i18n/index.js';

type VapidInfo = { public_key: string | null; push_configured: boolean };

function urlBase64ToUint8Array(base64: string): Uint8Array<ArrayBuffer> {
  const padding = '='.repeat((4 - (base64.length % 4)) % 4);
  const raw = atob((base64 + padding).replace(/-/g, '+').replace(/_/g, '/'));
  const out = new Uint8Array(new ArrayBuffer(raw.length));
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

/** Worker-side notification toggle: registers this device for Web Push (reminders arrive in Phase 2). */
export function PushToggle() {
  const supported =
    'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
  const [info, setInfo] = useState<VapidInfo | null>(null);
  const [subscribed, setSubscribed] = useState<boolean | null>(null);
  const [permission, setPermission] = useState<NotificationPermission>(
    supported ? Notification.permission : 'denied',
  );
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // iPhone Safari supports Web Push only for apps added to the home screen.
  const isIOS = /iPhone|iPad|iPod/i.test(navigator.userAgent);
  const standalone =
    window.matchMedia('(display-mode: standalone)').matches ||
    (navigator as Navigator & { standalone?: boolean }).standalone === true;
  const iosNotInstalled = isIOS && !standalone && !supported;

  const refresh = useCallback(async () => {
    try {
      setInfo(await api<VapidInfo>('/notifications/vapid-key'));
      if (supported) {
        const reg = await navigator.serviceWorker.ready;
        setSubscribed(!!(await reg.pushManager.getSubscription()));
      } else {
        setSubscribed(false);
      }
    } catch (err) {
      setError(errorMessage(err));
    }
  }, [supported]);
  useEffect(() => {
    void refresh();
  }, [refresh]);

  const enable = async () => {
    if (!info?.public_key) return;
    setBusy(true);
    setError(null);
    try {
      const perm = await Notification.requestPermission();
      setPermission(perm);
      if (perm !== 'granted') return;
      const reg = await navigator.serviceWorker.ready;
      const sub =
        (await reg.pushManager.getSubscription()) ??
        (await reg.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(info.public_key),
        }));
      await post('/notifications/subscribe', sub.toJSON());
      await refresh();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const disable = async () => {
    setBusy(true);
    setError(null);
    try {
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.getSubscription();
      if (sub) {
        await api('/notifications/subscribe', {
          method: 'DELETE',
          body: JSON.stringify({ endpoint: sub.endpoint }),
        });
        await sub.unsubscribe();
      }
      await refresh();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const canSubscribe =
    !iosNotInstalled && supported && permission !== 'denied' && info?.push_configured !== false;

  return (
    <div className="push-toggle">
      <h4>{t('push_title')}</h4>
      {/* What you get by turning them on, but only where there is something to turn on. Telling
          a worker to "turn on notifications" directly above "notifications are blocked" reads as
          the app arguing with itself. */}
      {canSubscribe && <p className="muted">{t('push_intro')}</p>}
      {error && <Alert>{error}</Alert>}
      {iosNotInstalled ? (
        <div>
          <p className="muted">{t('push_ios_install')}</p>
          <Link className="button small secondary" to="/add-to-home">
            {t('push_ios_how')}
          </Link>
        </div>
      ) : !supported ? (
        <p className="muted">{t('notif_unsupported')}</p>
      ) : permission === 'denied' ? (
        <p className="muted">{t('notif_blocked')}</p>
      ) : info && !info.push_configured ? (
        <p className="muted">{t('push_unavailable')}</p>
      ) : subscribed ? (
        <label className="inline switch">
          <input
            type="checkbox"
            checked
            onChange={disable}
            disabled={busy}
            aria-label={t('push_title')}
          />
          {t('push_on')}
        </label>
      ) : (
        <label className="inline switch">
          <input
            type="checkbox"
            checked={false}
            onChange={enable}
            disabled={busy || subscribed === null}
            aria-label={t('push_title')}
          />
          {t('push_off')}
        </label>
      )}
    </div>
  );
}
